import React, { useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Stack,
  Checkbox,
  CloseButton,
  VStack
} from '@chakra-ui/react'
import { useForm, FormProvider } from 'react-hook-form'
import FormInput from 'components/Chakra/FormInput'
import { IProcess } from 'interfaces/process'
import { useStores } from 'hooks/useStores'
import { CloseIcon } from '@chakra-ui/icons'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import { duplicateProcess } from 'API/process'
import routes from 'routes'
import { toast } from 'react-toastify'
import PrimaryButton from 'components/PrimaryButton'
import { MOBILE_FORM_FONT_SX } from 'constants/mobile-form'
import { AuthRoleIdEnum } from 'constants/user'
import {
  DARK_MODAL_CANCEL_BUTTON,
  DARK_MODAL_CONTENT,
  DARK_MODAL_OVERLAY,
  DARK_TABLE_BORDER,
  DARK_TEXT_MUTED,
  DARK_TEXT_PRIMARY
} from 'constants/dark-tokens'

interface ICopyProcessModalProps {
  isOpen: boolean
  onClose: () => void
  processId: number
  /** Opt-in dark-surface styling (default light — Process detail page stays unchanged). */
  dark?: boolean
}

export interface IProcessDuplicate {
  copyCollection?: boolean
  copyTags?: boolean
  copyNote?: boolean
  name: string
}

const CopyProcessModal = ({ isOpen, onClose, processId, dark = false }: ICopyProcessModalProps) => {
  const { processStore } = useStores()
  const { selectedProcess, process } = processStore
  const sourceProcess = selectedProcess?.id ? selectedProcess : process
  const name = sourceProcess?.name ?? 'Copy of drills'
  const methods = useForm<IProcessDuplicate>({
    defaultValues: {
      name: `Copy of ${name}`,
      copyCollection: true,
      copyTags: false,
      copyNote: false
    }
  })
  const navigate = useNavigate()

  const { handleSubmit } = methods

  useEffect(() => {
    return () => {
      methods.reset()
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      if (selectedProcess?.id || process?.id) {
        methods.reset({
          name: `Copy of ${name}`,
          copyCollection: true,
          copyTags: false,
          copyNote: false
        })
      }
    }
  }, [isOpen, selectedProcess, process])

  async function onSubmit(data: IProcessDuplicate) {
    try {
      const newProcess: IProcess = await duplicateProcess(processId, {
        copyCollection: data?.copyCollection,
        copyTags: data?.copyTags,
        copyNote: data?.copyNote,
        name: data?.name
      })
      if (newProcess?.id && processId !== newProcess?.id) {
        toast.success('Stack duplicated successfully')
        navigate(routes.processes.processId.value(String(newProcess?.id)))
      } else {
        throw new Error('Error duplicating stack')
      }
    } catch (error) {
      toast.error('Error duplicating stack')
    }
    onClose() // Close the modal after form submission
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" autoFocus>
      <ModalOverlay {...(dark ? DARK_MODAL_OVERLAY : {})} />
      <ModalContent {...(dark ? DARK_MODAL_CONTENT : {})} sx={MOBILE_FORM_FONT_SX}>
        <ModalCloseButton border="none" backgroundColor={dark ? 'transparent' : 'white'} />
        <ModalHeader
          fontWeight="500"
          fontSize="18px"
          lineHeight="28px"
          color={dark ? DARK_TEXT_PRIMARY : 'gray.800'}
          borderBottom={`1px solid ${dark ? DARK_TABLE_BORDER : '#E2E8F0'}`}
          fontFamily="Poppins"
        >
          Copy stack
        </ModalHeader>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody paddingY={6}>
              <Stack spacing={6}>
                <FormInput
                  name="name"
                  label="Stack name"
                  placeholder="Enter stack name"
                  isRequired={true}
                  rules={{ required: 'Stack name is required' }}
                  dark={dark}
                />
                <Stack spacing={1}>
                  <FormInput name="copyCollection">
                    <Checkbox color={dark ? DARK_TEXT_MUTED : 'gray.700'} {...methods.register('copyCollection')}>
                      Collection Inclusion
                    </Checkbox>
                  </FormInput>
                  <FormInput name="copyTags">
                    <Checkbox color={dark ? DARK_TEXT_MUTED : 'gray.700'} {...methods.register('copyTags')}>
                      Copy all existing tags
                    </Checkbox>
                  </FormInput>
                  <FormInput name="copyNote">
                    <Checkbox color={dark ? DARK_TEXT_MUTED : 'gray.700'} {...methods.register('copyNote')}>
                      Copy all notes
                    </Checkbox>
                  </FormInput>
                </Stack>
              </Stack>
            </ModalBody>

            {/* Double-submit fires two concurrent duplicate requests that race the
                backend's numeric-id allocation — lock the confirming button while
                submitting. */}
            <ModalFooter borderTop={`1px solid ${dark ? DARK_TABLE_BORDER : '#E2E8F0'}`} gap={4}>
              {dark ? (
                <>
                  <Button {...DARK_MODAL_CANCEL_BUTTON} onClick={onClose}>
                    Cancel
                  </Button>
                  <PrimaryButton type="submit" isLoading={methods.formState.isSubmitting}>
                    Save
                  </PrimaryButton>
                </>
              ) : (
                <>
                  <Button variant="white" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" isLoading={methods.formState.isSubmitting}>
                    Save
                  </Button>
                </>
              )}
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default observer(CopyProcessModal)
